import { defaultCurrencyCode } from '@/utils/intl';

// Kept in step with the website's shared/utils/currency.ts; the worker decides, this list only
// guards against a stale or hand-edited value arriving in the URL
export const supportedCurrencies = ['usd', 'eur', 'chf', 'gbp', 'aud', 'nzd', 'cad', 'aed', 'hkd'];

// The website worker answers from Cloudflare's country for the visitor's own request, so someone
// opening the app directly from Australia is served aud without ever visiting eventor.app
const geoURL = 'https://eventor.app/api/geo';
const storageKey = 'eventor_geo';
const maxCacheAge = 24 * 60 * 60 * 1000;
const requestTimeout = 1500;

export function isSupportedCurrency(code) {
  return typeof code === 'string' && supportedCurrencies.includes(code.trim().toLowerCase());
}

function normalize(code) {
  return isSupportedCurrency(code) ? code.trim().toLowerCase() : '';
}

/** Currency handed over by a link from the website (…/auth/signin?cur=aud) */
export function currencyFromLocation(search = window.location.search) {
  try {
    return normalize(new URLSearchParams(search).get('cur'));
  } catch (error) {
    return '';
  }
}

export function readCachedGeo() {
  try {
    const cached = JSON.parse(window.localStorage.getItem(storageKey) || 'null');

    if (!cached || !isSupportedCurrency(cached.currency)) return null;
    if (Date.now() - (cached.savedAt || 0) > maxCacheAge) return null;

    return cached;
  } catch (error) {
    return null;
  }
}

function writeCachedGeo(geo) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify({ ...geo, savedAt: Date.now() }));
  } catch (error) {
    // Private mode and full storage are not worth failing a page load over
  }
}

export async function fetchGeo() {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), requestTimeout) : null;

  try {
    const response = await fetch(geoURL, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    });

    if (!response.ok) return null;

    const geo = await response.json();

    if (!isSupportedCurrency(geo.currency)) return null;

    const result = { currency: normalize(geo.currency), country: geo.country || null, source: 'edge' };
    writeCachedGeo(result);

    return result;
  } catch (error) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Currency for this visitor, most trustworthy source first:
 * a currency already on the profile (their past payments used it), the link they arrived by,
 * the edge answer, yesterday's answer, and finally the default.
 */
export async function resolveCurrency(profileCurrency) {
  if (isSupportedCurrency(profileCurrency)) {
    return { currency: normalize(profileCurrency), country: null, source: 'profile' };
  }

  const fromLink = currencyFromLocation();
  if (fromLink) return { currency: fromLink, country: null, source: 'link' };

  const fresh = await fetchGeo();
  if (fresh) return fresh;

  const cached = readCachedGeo();
  if (cached) return { currency: cached.currency, country: cached.country || null, source: 'cache' };

  return { currency: defaultCurrencyCode, country: null, source: 'default' };
}
